import { type ReactNode, useState } from "react";

import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  SearchAutocomplete,
  type SearchAutocompleteProps,
  type SearchItem,
} from "../SearchAutocomplete/SearchAutocomplete";

import { UserSearchSkeleton } from "./UserSearchSkeleton";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type SearchUserItem = SearchItem & {
  user: User;
};

type SearchUserProps<T extends SearchUserItem> = Omit<
  SearchAutocompleteProps<T>,
  "items"
> & {
  users: T[];
  disabledTooltip?: ReactNode | string;
  onUserSelect?: (item: T | null) => void;
  onChange?: (value: string) => void;
  noPadding?: boolean;
  clearOnSelect?: boolean;
  error?: string;
  "data-testid"?: string;
  showAllOnFocus?: boolean;
  // Infinite scroll props
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
};

const getUserSkeleton = () => <UserSearchSkeleton />;

const highlightText = (text: string, highlight: string) => {
  if (!highlight.trim()) {
    return text;
  }

  const regex = new RegExp(`(${highlight})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <span className="text-text/primary" key={index}>
        {part}
      </span>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index}>{part}</span>
    ),
  );
};

const isEmailLikeSearch = (searchTerm: string) =>
  searchTerm.includes("@") ||
  searchTerm.endsWith(".") ||
  /\w+\.\w+/.test(searchTerm);

export const SearchUser = <T extends SearchUserItem>({
  users,
  isLoading = false,
  disabledTooltip,
  onUserSelect,
  onChange,
  placeholder,
  className,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  noPadding = false,
  clearOnSelect = false,
  showAllOnFocus = false,
  error,
  "data-testid": dataTestId,
  ...props
}: SearchUserProps<T>) => {
  const [searchValue, setSearchValue] = useState("");

  const handleItemSelect = (item: T | null) => {
    onUserSelect?.(item);

    if (clearOnSelect) {
      setSearchValue("");
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onChange?.(value);
  };

  const renderUserItem = (item: SearchUserItem) => {
    const fullName = `${item.user.firstName} ${item.user.lastName}`.trim();
    const isEmailSearch = isEmailLikeSearch(searchValue);

    return (
      <div className="flex items-center gap-2">
        <Avatar initials={item.user.firstName} />
        <div>
          <div className="font-medium" data-testid="searchUser-userItem-name">
            {isEmailSearch ? fullName : highlightText(fullName, searchValue)}
          </div>
          <div
            className="body-small-regular text-text/secondary"
            data-testid="searchUser-search-autocomplete-email"
          >
            {highlightText(item.user.email, searchValue)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full" data-testid={dataTestId}>
      <SearchAutocomplete<T>
        noPadding={noPadding}
        {...props}
        className={className}
        clearOnSelect={clearOnSelect}
        disabledTooltip={disabledTooltip}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        items={users}
        name="searchUser"
        onChange={handleSearchChange}
        onItemSelect={handleItemSelect}
        onLoadMore={onLoadMore}
        placeholder={placeholder ?? m.placeholders_searchUsers()}
        renderItem={renderUserItem}
        renderSkeleton={getUserSkeleton}
        showAllOnFocus={showAllOnFocus}
      />
      {isTruthy(error) && (
        <ValidationErrorText dataTestId="searchUser" withMargin>
          {error}
        </ValidationErrorText>
      )}
    </div>
  );
};
